// JSON-RPC reads. Order per chain: the same-origin relay (api/rpc.js, keyed upstream from the operator's
// environment), then the public endpoints. The relay is dropped for the session once it answers 404 or
// 5xx, so a local file or a python http.server costs one failed request, not one per call. Every endpoint
// is asked for eth_chainId once before use; a wrong-chain node is skipped instead of answering "0x".
import { CHAINS, MAINNET, PROXY, type ChainId } from './chain';
import { errorText } from './format';

export const RPC_TIMEOUT = 8000;

declare global {
  interface Window {
    BELLGUARD_RPC_PROXY?: boolean;
    BELLGUARD_API_BASE?: string;
  }
}

export class HttpError extends Error {
  constructor(public readonly status: number) {
    super(`HTTP ${status}`);
    this.name = 'HttpError';
  }
}

interface RpcAnswer {
  result?: unknown;
  error?: { message?: string };
}

export async function post(url: string, method: string, params: unknown[], ms: number): Promise<unknown> {
  const ctl = new AbortController();
  const timer = setTimeout(() => { ctl.abort(); }, ms);
  try {
    const r = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
      signal: ctl.signal,
    });
    if (!r.ok) throw new HttpError(r.status);
    const j = (await r.json()) as RpcAnswer;
    if (j.error) throw new Error(j.error.message ?? 'rpc error');
    if (!('result' in j)) throw new Error('no result in answer');
    return j.result;
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw new Error(`no answer within ${ms / 1000} s`);
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

const relayProbe = new Map<ChainId, Promise<boolean>>();
const chainChecked = new Set<string>();
const rpcLabel = (url: string): string => (url.startsWith(PROXY) ? 'site relay' : new URL(url).hostname);

const localHost = typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);

// One probe per chain per page load, shared by every concurrent caller: the relay is used only after it
// has answered eth_chainId with the right chain. On localhost the relay is only probed when
// window.BELLGUARD_RPC_PROXY is set, so a plain static server does not log a failed POST per chain.
function relayAlive(chainId: ChainId): Promise<boolean> {
  let p = relayProbe.get(chainId);
  if (!p) {
    const url = `${PROXY}?chain=${chainId}`;
    const skip = typeof location === 'undefined' || location.protocol === 'file:' || (localHost && !window.BELLGUARD_RPC_PROXY);
    p = skip
      ? Promise.resolve(false)
      : post(url, 'eth_chainId', [], RPC_TIMEOUT)
          .then((id) => {
            const good = String(id).toLowerCase() === CHAINS[chainId].hex;
            if (good) chainChecked.add(url);
            return good;
          })
          .catch(() => false);
    relayProbe.set(chainId, p);
  }
  return p;
}

export async function rpc(method: string, params: unknown[], chainId: ChainId = MAINNET): Promise<unknown> {
  const list = (await relayAlive(chainId)) ? [`${PROXY}?chain=${chainId}`, ...CHAINS[chainId].rpcs] : [...CHAINS[chainId].rpcs];
  const errors: string[] = [];
  for (const url of list) {
    try {
      if (!chainChecked.has(url)) {
        const id = await post(url, 'eth_chainId', [], RPC_TIMEOUT);
        if (String(id).toLowerCase() !== CHAINS[chainId].hex) throw new Error(`wrong chain (${String(id)})`);
        chainChecked.add(url);
      }
      return await post(url, method, params, RPC_TIMEOUT);
    } catch (e) {
      errors.push(`${rpcLabel(url)}: ${errorText(e)}`);
    }
  }
  throw new Error(errors.join('; ') || 'no RPC endpoint');
}

export const call = (to: string, data: string, chainId: ChainId = MAINNET): Promise<unknown> =>
  rpc('eth_call', [{ to, data }, 'latest'], chainId);

/** eth_call answers that must carry n words. A throttled or misconfigured node answers "0x", which
 *  must never be decoded as "false" or "zero". */
export async function callWords(to: string, data: string, n: number, chainId: ChainId = MAINNET): Promise<string> {
  const r = await call(to, data, chainId);
  if (typeof r !== 'string' || !r.startsWith('0x')) throw new Error('malformed answer from the RPC');
  if (r === '0x') throw new Error('empty answer ("0x") from the RPC, which usually means a throttled node or a call that reverted');
  if (r.length < 2 + 64 * n) throw new Error(`short answer from the RPC (${(r.length - 2) / 2} bytes)`);
  return r;
}
export const callWord = (to: string, data: string, chainId: ChainId = MAINNET): Promise<string> => callWords(to, data, 1, chainId);

export async function fetchJson(url: string, ms: number): Promise<unknown> {
  const ctl = new AbortController();
  const timer = setTimeout(() => { ctl.abort(); }, ms);
  try {
    const r = await fetch(url, { signal: ctl.signal, headers: { accept: 'application/json' } });
    if (!r.ok) throw new HttpError(r.status);
    return (await r.json()) as unknown;
  } finally {
    clearTimeout(timer);
  }
}
